import { useCallback, useContext } from 'react'
import type { AriaAttributes, ReactNode } from 'react'

import { warn } from '../../../../shared/helpers'
import useMountEffect from '../../../../shared/helpers/useMountEffect'
import useMounted from '../../../../shared/helpers/useMounted'
import type { HeightAnimationAllProps } from '../../../../components/HeightAnimation'
import HeightAnimation from '../../../../components/HeightAnimation'
import FieldProvider from '../../Field/Provider'
import useVisibility from './useVisibility'
import VisibilityContext from './VisibilityContext'
import SummaryListContext from '../../Value/SummaryList/SummaryListContext'

import type { Path, UseFieldProps } from '../../types'
import type { DataAttributes } from '../../hooks/useFieldProps'
import type { FilterData } from '../../DataContext'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

export type VisibleWhen =
  | {
      path: Path
      hasValue: unknown | ((value: unknown) => boolean)
    }
  | {
      itemPath: Path
      hasValue: unknown | ((value: unknown) => boolean)
    }
  | {
      path: Path
      isValid: boolean
      validateContinuously?: boolean
    }
  | {
      itemPath: Path
      isValid: boolean
      validateContinuously?: boolean
    }

export type FormVisibilityProps = {
  /** A unique identifier for the component's wrapper element. */
  id?: string
  visible?: boolean
  /** Given data context path must be defined to show children */
  pathDefined?: Path
  /** Given data context path must be undefined to show children */
  pathUndefined?: Path
  /** Given data context path must be truthy to show children */
  pathTruthy?: Path
  /** Given data context path must be falsy to show children */
  pathFalsy?: Path
  /** Given data context path must be true to show children */
  pathTrue?: Path
  /** Given data context path must be false to show children */
  pathFalse?: Path
  /** Provide a `path` or `itemPath` and a `hasValue` method that returns a boolean or the expected value in order to show children. The first parameter is the value of the path. */
  visibleWhen?: VisibleWhen
  /** Same as `visibleWhen`, but with inverted logic. */
  visibleWhenNot?: VisibleWhen
  /** Infer visibility calling given derivative function with the whole data set. Should return true/false for visibility.   */
  inferData?: (data: unknown) => boolean
  /** Filter data based on provided criteria. The first parameter is the path, the second is the value, and the third is the props, and the fourth is the internal. Return false to filter out the data. */
  filterData?: FilterData
  /** Callback for when the content gets visible. */
  onVisible?: HeightAnimationAllProps['onOpen']

  /** When visibility is hidden, and `keepInDOM` is true, pass these props to the children */
  fieldPropsWhenHidden?: UseFieldProps & DataAttributes & AriaAttributes
  children: ReactNode

  /** For internal use only. Used by "Iterate.Visibility" */
  withinIterate?: boolean
} & Pick<
  HeightAnimationAllProps,
  | 'onAnimationEnd'
  | 'animate'
  | 'keepInDOM'
  | 'element'
  | 'compensateForGap'
>

function Visibility(props: FormVisibilityProps) {
  const {
    id,
    visible,
    pathDefined,
    pathUndefined,
    pathTruthy,
    pathFalsy,
    pathTrue,
    pathFalse,
    visibleWhen,
    visibleWhenNot,
    inferData,
    filterData,
    onVisible,
    onAnimationEnd,
    animate,
    keepInDOM,
    compensateForGap,
    fieldPropsWhenHidden,
    withinIterate,
    children,
    ...rest
  } = props

  useMountEffect(() => {
    if (fieldPropsWhenHidden && !keepInDOM) {
      warn('Using "fieldPropsWhenHidden" requires "keepInDOM" to be true.')
    }
  })

  const { check } = useVisibility({
    visible,
    withinIterate,
    pathDefined,
    pathUndefined,
    pathTruthy,
    pathFalsy,
    pathTrue,
    pathFalse,
    visibleWhen,
    visibleWhenNot,
    inferData,
    filterData,
  })
  const open = check()
  const content = (
    <VisibilityContext
      value={{
        isVisible: open,
        keepInDOM,
        props, // Used by ValueBlock and for when nested in a Visibility
      }}
    >
      {children}
    </VisibilityContext>
  )
  const mountedRef = useMounted()

  const onOpen: HeightAnimationAllProps['onOpen'] = useCallback(
    (state) => {
      if (mountedRef.current) {
        onVisible?.(state)
      }
    },
    [mountedRef, onVisible]
  )

  const summaryListContext = useContext(SummaryListContext)
  const providerProps = !open ? fieldPropsWhenHidden : null

  if (
    (animate || keepInDOM) &&
    summaryListContext &&
    !summaryListContext.isNested
  ) {
    // Handle the animation inside the SummaryList
    return <FieldProvider {...providerProps}>{content}</FieldProvider>
  }

  if (animate) {
    return (
      <HeightAnimation
        id={id}
        open={open}
        onAnimationEnd={onAnimationEnd}
        onOpen={onOpen}
        keepInDOM={Boolean(keepInDOM)}
        className="dnb-forms-visibility"
        compensateForGap={compensateForGap}
        {...rest}
      >
        <FieldProvider {...providerProps}>{content}</FieldProvider>
      </HeightAnimation>
    )
  }

  if (mountedRef.current) {
    onVisible?.(open)
  }

  if (keepInDOM) {
    return (
      <span id={id} className="dnb-forms-visibility" hidden={!open}>
        <FieldProvider {...providerProps}>{content}</FieldProvider>
      </span>
    )
  }

  if (id) {
    return (
      <>
        {open ? (
          <span id={id} className="dnb-forms-visibility">
            {content}
          </span>
        ) : null}
      </>
    )
  }

  return <>{open ? content : null}</>
}

withComponentMarkers(Visibility, {
  _supportsSpacingProps: 'children',
})

export default Visibility
